import { captureException } from '@sentry/sveltekit';
import { afterLogout } from './logout';
import { session } from '$lib/stores';
import { BASE_APP_URL } from '$lib/constants';
import { browser } from '$app/environment';
import { getApolloClient } from '$lib/apollo';
import { PUBLIC_METAFY_DEPLOY_VERSION } from '$env/static/public';

/** Headers to pass to `fetch` */
const headers = {
  'content-type': 'application/json',
  'x-metafy-client': browser ? 'web' : 'node',
  'x-metafy-client-version': PUBLIC_METAFY_DEPLOY_VERSION
};
const requestsInFlight: Set<string> = new Set();

/**
 * Refresh the JWT token for the authenticated user.
 */
export async function refreshToken() {
  // Get the tokens from the session store
  const { accessToken, refreshToken } = session.get();

  try {
    // If another request is attempting to refresh the authentication token then
    // wait for it to finish.
    if (requestsInFlight.has(refreshToken!)) {
      await new Promise<void>(resolve => {
        // Check every 50ms if the request has finished.
        setInterval(() => {
          if (!requestsInFlight.has(refreshToken!)) {
            resolve();
          }
        }, 50);
      });
      // return as we don't want to refresh an already refreshed token. We can
      // to the original context as the auth tokens have been updated.
      return;
    }

    // Continue on with the refresh request
    requestsInFlight.add(refreshToken!);
    const response = await fetch(`${BASE_APP_URL}/auth/proxy/refresh.json`, {
      method: 'post',
      body: JSON.stringify({ access_token: accessToken, refresh_token: refreshToken }),
      headers
    });

    // Request failed. Reset the session and throw an error
    if (response.status >= 400) {
      session.set({ userAgent: '', apolloClient: getApolloClient() });
      throw new Error('Unauthenticated');
    }

    // Success! 🚀 Update the tokens in our global store.
    const json = await response.json();
    session.update(s => ({ ...s, accessToken: json.accessToken, refreshToken: json.refreshToken }));
  } finally {
    requestsInFlight.delete(refreshToken!);
  }
}

/**
 * Fetch proxy that handles refreshing an invalid auth token, and rate limiting.
 */
export async function customFetch(endpoint: URL | RequestInfo, options: RequestInit = {}): Promise<Response> {
  let ahoyHeaders = {};
  if (browser) {
    const ahoy = (await import('ahoy.js')).default;
    ahoyHeaders = {
      'Ahoy-Visit': ahoy.getVisitId(),
      'Ahoy-Visitor': ahoy.getVisitorId()
    };
  }
  const fetchHeaders: Record<string, any> = {
    ...headers,
    ...ahoyHeaders,
    ...options.headers
  };
  const sessionData = session.get();
  const { accessToken } = sessionData ?? {};

  // Add authorization header
  const isRefreshRequest = 'x-metafy-refresh' in fetchHeaders;
  if (!isRefreshRequest && !fetchHeaders.authorization && accessToken) {
    fetchHeaders.authorization = `Bearer ${accessToken}`;
  }

  // Do the request!
  const response = await fetch(endpoint, {
    ...options,
    headers: fetchHeaders
  });

  if (response.status >= 400) {
    try {
      // Attempt to refresh our access_token if we have an expired one.
      // Clone the response so we don't consume the buffer on the original
      // that we will return to the caller.
      const clone = response.clone();
      const text = await clone.text();
      if (text.includes('expired JWT access token')) {
        // Refresh our access token and retry the request
        await refreshToken();
        return customFetch(endpoint, options);
      }
    } catch (error) {
      if (browser && (error as Error).message === 'Unauthenticated') {
        afterLogout();
        window.location.assign('/auth/account/login');
        return response;
      } else {
        captureException(error);
      }
      return response;
    }
  }

  // It was a good response! 🔥
  return response;
}
